/* ============================================================================
   Stat tile: etiqueta · valor · delta · sparkline.
   Contrato del componente, en ese orden y sin excepciones.
   El valor usa cifras proporcionales (nunca tabular-nums a tamaño display).
   ========================================================================== */

import { Sparkline } from '../charts/Sparkline'
import { Delta } from './Delta'
import { useSpringNumber } from '../lib/motion'
import './statTile.css'

const VALUE_SPRING = { damping: 1, response: 0.3 } as const

export interface StatTileProps {
  label: string
  /** Valor numérico. Se anima con resorte al cambiar de periodo. */
  value: number
  /** Cómo se imprime el valor. */
  format: (value: number) => string
  unit?: string
  delta?: number
  deltaKind?: 'percent' | 'currency'
  deltaSince?: string
  /** Subir es favorable para esta métrica. */
  invertDelta?: boolean
  trend?: number[]
  /** Nota corta bajo el valor: fuente, advertencia, cobertura. */
  note?: string
  /** Resalta el tile como el principal de su fila. */
  emphasis?: boolean
  /** Color del punto de la sparkline. */
  accent?: string
}

export function StatTile({
  label,
  value,
  format,
  unit,
  delta,
  deltaKind = 'percent',
  deltaSince,
  invertDelta = false,
  trend,
  note,
  emphasis = false,
  accent,
}: StatTileProps) {
  const animated = useSpringNumber(value, VALUE_SPRING)

  return (
    <div className={`stat${emphasis ? ' stat--emphasis' : ''}`}>
      <p className="stat__label t-footnote ink-2">{label}</p>
      <div className="stat__row">
        <p className="stat__value">
          {format(animated)}
          {unit ? <span className="stat__unit t-footnote ink-3">{unit}</span> : null}
        </p>
        {trend && trend.length > 2 ? (
          <Sparkline values={trend} accent={accent ?? 'var(--accent)'} />
        ) : null}
      </div>
      {delta !== undefined ? (
        <Delta value={delta} kind={deltaKind} since={deltaSince} invert={invertDelta} />
      ) : null}
      {note ? <p className="stat__note t-caption ink-3">{note}</p> : null}
    </div>
  )
}
